import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Fade,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShieldIcon from '@mui/icons-material/Shield';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CircleAddIcon from '@mui/icons-material/AddCircle';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { STATUS_ICONS } from '../../constants/gameConstants';
import { scrollbarStyles } from '../shared/StyledComponents';
import EncounterGeneratorModal from '../shared/EncounterGeneratorModal';
import DmPartySettingsModal from '../shared/DmPartySettingsModal';
import MonsterTooltipContent from '../shared/MonsterTooltipContent';
import MonsterInfoModal from '../shared/MonsterInfoModal';
import { getMonsterDetails, getMonsterRawByIndex } from '../../services/monsterService';
import EncounterDifficultyBar from '../shared/EncounterDifficultyBar';
import {
  allEnemyTokensAreSrd,
  getLiveEncounterDifficultyFromCoins,
} from '../../utils/encounterUtils';
import { useGame } from '../../context/GameContext';
import { useDmPartyDefaults } from '../../hooks/useDmPartyDefaults';

const PanelContainer = styled(Box)(({ theme }) => ({
  width: '280px',
  height: '100%',
  backgroundColor: theme.palette.background.light,
  borderRight: `2px solid ${theme.palette.background.dark}`,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  paddingBottom: '45px',
}));

const PanelHeader = styled(Box)(({ theme }) => ({
  padding: '12px 16px',
  borderBottom: `2px solid ${theme.palette.background.dark}`,
  backgroundColor: theme.palette.background.main,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const PanelTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'MedievalSharp',
  fontSize: '1.1rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)',
}));

const TokenList = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: '8px',
  ...scrollbarStyles(theme),
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: 'transparent',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme, tokentype }) => ({
  backgroundColor: tokentype === 'enemy' 
    ? 'rgba(139, 0, 0, 0.15)' 
    : 'rgba(76, 175, 80, 0.15)',
  borderRadius: '6px',
  minHeight: '40px',
  marginBottom: '4px',
  '&.Mui-expanded': {
    minHeight: '40px',
  },
  '& .MuiAccordionSummary-content': {
    margin: '8px 0',
    '&.Mui-expanded': {
      margin: '8px 0',
    },
  },
}));

const AccordionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'MedievalSharp',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const TokenCount = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginLeft: '8px',
}));

const TokenCard = styled(Box)(({ theme, isselected, isdead, isdragging, isremoved, ishidden }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '6px',
  padding: '10px',
  marginBottom: '6px',
  borderRadius: '6px',
  backgroundColor: isremoved === 'true'
    ? 'rgba(100, 100, 100, 0.15)'
    : isselected === 'true'
      ? 'rgba(218, 165, 32, 0.2)'
      : 'rgba(255, 255, 255, 0.05)',
  border: isremoved === 'true'
    ? '2px dashed rgba(150, 150, 150, 0.5)'
    : isselected === 'true'
      ? `2px solid ${theme.palette.secondary.main}`
      : '2px solid transparent',
  cursor: isdragging === 'true' ? 'grabbing' : 'grab',
  opacity: isdead === 'true' ? 0.5 : (isdragging === 'true' ? 0.4 : (ishidden === 'true' ? 0.7 : 1)),
  transition: isdragging === 'true' ? 'none' : 'all 0.15s ease',
  userSelect: 'none',
  '&:hover': {
    backgroundColor: isselected === 'true'
      ? 'rgba(218, 165, 32, 0.25)'
      : 'rgba(255, 255, 255, 0.1)',
    borderColor: isremoved === 'true' ? 'rgba(150, 150, 150, 0.7)' : theme.palette.secondary.light,
  },
}));

const RemovedBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '4px',
  right: '4px',
  backgroundColor: 'rgba(100, 100, 100, 0.8)',
  color: '#fff',
  padding: '2px 6px',
  borderRadius: '8px',
  fontSize: '0.6rem',
  fontFamily: 'Merriweather',
  fontWeight: 600,
  textTransform: 'uppercase',
}));

const HiddenBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '4px',
  left: '4px',
  backgroundColor: 'rgba(156, 39, 176, 0.8)',
  color: '#fff',
  padding: '2px 6px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  '& .MuiSvgIcon-root': {
    fontSize: '1rem',
  },
}));

const DragHint = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '-8px',
  left: '50%',
  transform: 'translateX(-50%)',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  padding: '4px 10px',
  borderRadius: '12px',
  fontSize: '0.7rem',
  fontFamily: 'Merriweather',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  zIndex: 10,
  '& .MuiSvgIcon-root': {
    fontSize: '0.9rem',
  },
}));

const TokenCardWrapper = styled(Box)({
  position: 'relative',
});

const TokenAvatar = styled(Box)(({ theme, bgcolor, image }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  backgroundColor: bgcolor || theme.palette.grey[500],
  backgroundImage: image ? `url(${image})` : 'none',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  border: `2px solid ${theme.palette.background.dark}`,
  flexShrink: 0,
}));

const TokenInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const TokenName = styled(Typography)(({ theme }) => ({
  fontFamily: 'MedievalSharp',
  fontSize: '0.9rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const TokenStats = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
});

const StatItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

const StatIcon = styled(Box)(({ theme, color }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: '0.9rem',
    color: color || theme.palette.text.secondary,
  },
}));

const StatValue = styled(Typography)(({ theme, warning }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: warning ? theme.palette.error.main : theme.palette.text.secondary,
}));

const StatusContainer = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '4px',
  marginTop: '4px',
});

const StatusBadge = styled(Box)(({ theme }) => ({
  width: '18px',
  height: '18px',
  borderRadius: '3px',
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& img': {
    width: '14px',
    height: '14px',
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: '16px',
  textAlign: 'center',
}));

const EmptyText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Merriweather',
  fontSize: '0.8rem',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
}));

const ENEMY_MONSTER_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'background.light',
      border: '1px solid',
      borderColor: 'background.dark',
      boxShadow: 4,
      p: 0,
    },
  },
  arrow: {
    sx: {
      color: 'background.light',
    },
  },
};

function coinToTooltipMonster(coin) {
  const idx = coin.monsterData?.index;
  if (idx) {
    const m = getMonsterDetails(idx);
    if (m) {
      return {
        name: m.name,
        size: m.size,
        type: m.type,
        alignment: m.alignment,
        img: m.img || coin.image,
        ac: m.ac ?? coin.armorClass,
        hp: m.hp ?? coin.maxHealth,
        cr: m.cr,
        strength: m.strength,
        dexterity: m.dexterity,
        constitution: m.constitution,
      };
    }
  }
  return {
    name: coin.name || 'Creature',
    size: 'Medium',
    type: 'creature',
    alignment: null,
    img: coin.image,
    ac: coin.armorClass ?? '—',
    hp: coin.maxHealth ?? '—',
    cr: coin.monsterData?.cr ?? '—',
    strength: null,
    dexterity: null,
    constitution: null,
  };
}

const TokenPanel = () => {
  const {
    coins = [],
    selectedCoinId,
    setSelectedCoinId: onSelectCoin,
    canManageEnemies = false,
    isCreator = false,
    setShowColorModal,
    handleAddEncounterMonsters: onAddEncounterMonsters,
    onTokenDragStart,
    onTokenDragEnd,
    onDeleteTokenFromPanel: onDeleteToken,
    newlyCreatedCoinId,
    setNewlyCreatedCoinId,
  } = useGame();

  const onAddCharacter = () => setShowColorModal(true);
  const onDismissNewCoinHint = useCallback(() => setNewlyCreatedCoinId(null), [setNewlyCreatedCoinId]);
  const [friendlyExpanded, setFriendlyExpanded] = useState(true);
  const [enemyExpanded, setEnemyExpanded] = useState(true);
  const [draggingCoinId, setDraggingCoinId] = useState(null);
  const [showHintForCoin, setShowHintForCoin] = useState(null);
  const [encounterModalOpen, setEncounterModalOpen] = useState(false);
  const [partySettingsOpen, setPartySettingsOpen] = useState(false);
  const [dmParty, setDmParty] = useDmPartyDefaults();
  const [enemyMenuAnchor, setEnemyMenuAnchor] = useState(null);
  const [enemyMenuCoin, setEnemyMenuCoin] = useState(null);
  const [monsterInfoOpen, setMonsterInfoOpen] = useState(false);
  const [monsterInfoCoin, setMonsterInfoCoin] = useState(null);
  const dragStartPosRef = useRef(null);
  const isDraggingRef = useRef(false);

  const friendlyTokens = coins.filter(coin => !coin.isEnemy);
  const enemyTokens = coins.filter(coin => coin.isEnemy);

  // Auto-dismiss the hint tooltip for newly created coins
  useEffect(() => {
    if (newlyCreatedCoinId) {
      setShowHintForCoin(newlyCreatedCoinId);
      const timer = setTimeout(() => {
        setShowHintForCoin(null);
        onDismissNewCoinHint?.();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [newlyCreatedCoinId, onDismissNewCoinHint]);

  const handleTokenClick = (coin) => {
    if (isDraggingRef.current) return;
    if (onSelectCoin) {
      if (selectedCoinId === coin.id) {
        onSelectCoin(null);
      } else {
        onSelectCoin(coin.id);
      }
    }
  };

  const closeEnemyMenu = useCallback(() => {
    setEnemyMenuAnchor(null);
    setEnemyMenuCoin(null);
  }, []);

  const handleEnemyMenuOpen = useCallback((e, coin) => {
    e.stopPropagation();
    e.preventDefault();
    setEnemyMenuAnchor(e.currentTarget);
    setEnemyMenuCoin(coin);
  }, []);

  const handleMouseDown = useCallback((e, coin) => {
    if (e.button !== 0) return;
    e.preventDefault();
    
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };
    isDraggingRef.current = false;
    
    const handleMouseMove = (moveEvent) => {
      if (!dragStartPosRef.current) return;
      
      const dx = moveEvent.clientX - dragStartPosRef.current.x;
      const dy = moveEvent.clientY - dragStartPosRef.current.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      if (distance > 5 && !isDraggingRef.current) {
        isDraggingRef.current = true;
        setDraggingCoinId(coin.id);
        onTokenDragStart?.(coin, moveEvent.clientX, moveEvent.clientY);
      }
      
      if (isDraggingRef.current) {
        onTokenDragStart?.(coin, moveEvent.clientX, moveEvent.clientY);
      }
    };
    
    const handleMouseUp = (upEvent) => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      if (isDraggingRef.current) {
        onTokenDragEnd?.(coin, upEvent.clientX, upEvent.clientY);
        setDraggingCoinId(null);
        setTimeout(() => {
          isDraggingRef.current = false;
        }, 50);
      }
      
      dragStartPosRef.current = null;
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [onTokenDragStart, onTokenDragEnd]);

  const renderToken = (coin) => {
    const currentHealth = coin.currentHealth ?? coin.maxHealth;
    const healthPercent = coin.maxHealth ? (currentHealth / coin.maxHealth) : 1;
    const isDead = coin.maxHealth && currentHealth <= 0;
    const isLowHealth = healthPercent <= 0.25 && !isDead;
    const isBeingDragged = draggingCoinId === coin.id;
    const showDragHint = showHintForCoin === coin.id;
    const isRemoved = coin.removed || coin.x === undefined || coin.y === undefined;
    const isHidden = coin.hidden;

    const tokenMain = (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minWidth: 0,
          gap: '10px',
          alignItems: 'flex-start',
        }}
      >
        <TokenAvatar bgcolor={coin.color} image={coin.image} />
        <TokenInfo>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.75,
              flexWrap: 'wrap',
              minWidth: 0,
            }}
          >
            <TokenName sx={{ flex: '1 1 auto', minWidth: 0 }}>{coin.name || 'Unnamed'}</TokenName>
            
            {!coin.isEnemy && coin.isPc === false && (
              <Typography
                component="span"
                variant="caption"
                sx={{ fontFamily: 'Merriweather', color: 'text.secondary', flexShrink: 0 }}
              >
                NPC
              </Typography>
            )}
          </Box>
          <TokenStats>
            {coin.maxHealth && (
              <Tooltip title="Health" placement="top">
                <StatItem>
                  <StatIcon color={isLowHealth ? '#F44336' : '#4CAF50'}>
                    <FavoriteIcon />
                  </StatIcon>
                  <StatValue warning={isLowHealth}>
                    {currentHealth}/{coin.maxHealth}
                  </StatValue>
                </StatItem>
              </Tooltip>
            )}
            {coin.armorClass && (
              <Tooltip title="Armor Class" placement="top">
                <StatItem>
                  <StatIcon color="#607D8B">
                    <ShieldIcon />
                  </StatIcon>
                  <StatValue>{coin.armorClass}</StatValue>
                </StatItem>
              </Tooltip>
            )}
          </TokenStats>
          {coin.statuses && coin.statuses.length > 0 && (
            <StatusContainer>
              {coin.statuses.map((status, index) => (
                <Tooltip key={index} title={status} placement="top">
                  <StatusBadge>
                    {STATUS_ICONS[status] && (
                      <img src={STATUS_ICONS[status]} alt={status} />
                    )}
                  </StatusBadge>
                </Tooltip>
              ))}
            </StatusContainer>
          )}
        </TokenInfo>
      </Box>
    );

    return (
      <TokenCardWrapper key={coin.id}>
        <TokenCard
          isselected={selectedCoinId === coin.id ? 'true' : 'false'}
          isdead={isDead ? 'true' : 'false'}
          isdragging={isBeingDragged ? 'true' : 'false'}
          isremoved={isRemoved ? 'true' : 'false'}
          ishidden={isHidden ? 'true' : 'false'}
          onClick={() => handleTokenClick(coin)}
          onMouseDown={(e) => handleMouseDown(e, coin)}
        >
          {coin.isEnemy && canSeeEnemies ? (
            <Tooltip
              title={<MonsterTooltipContent monster={coinToTooltipMonster(coin)} />}
              placement="left"
              arrow
              enterDelay={300}
              slotProps={ENEMY_MONSTER_TOOLTIP_SLOT_PROPS}
            >
              {tokenMain}
            </Tooltip>
          ) : (
            tokenMain
          )}
          {canSeeEnemies && coin.isEnemy && (
            <IconButton
              size="small"
              aria-label="Enemy token actions"
              onClick={(e) => handleEnemyMenuOpen(e, coin)}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{
                flexShrink: 0,
                mt: '-4px',
                mr: '-6px',
                color: 'text.secondary',
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </TokenCard>
        {/* Hidden badge (takes priority over removed badge visually) */}
        {isHidden && (
          <HiddenBadge>
            <VisibilityOffIcon />
          </HiddenBadge>
        )}
        <Fade in={showDragHint} timeout={300}>
          <DragHint>
            <OpenWithIcon />
            Drag token to the Grid
          </DragHint>
        </Fade>
      </TokenCardWrapper>
    );
  };

  const canSeeEnemies = isCreator || canManageEnemies;

  const liveEncounter = useMemo(
    () => getLiveEncounterDifficultyFromCoins(coins, dmParty),
    [coins, dmParty]
  );
  const enemiesAreAllSrd = useMemo(() => allEnemyTokensAreSrd(coins), [coins]);

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>Tokens</PanelTitle>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {onAddCharacter && (
            <Tooltip title="Add Token">
              <IconButton 
                onClick={onAddCharacter}
                size="small"
                sx={{ color: 'primary.main' }}
              >
                <CircleAddIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </PanelHeader>
      <TokenList>
      {canSeeEnemies && (
          <Box sx={{ mb: 2 }}>
            <Button
              onClick={() => setEncounterModalOpen(true)}
              fullWidth
              variant="outlined"
              size="small"
              sx={{ color: 'secondary.main', fontSize: '0.8rem' }}
              startIcon={<AutoFixHighIcon />}
            >
              Encounter Generator
            </Button>
            {enemiesAreAllSrd && liveEncounter.ready && enemyTokens.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="text.secondary" component="div" sx={{ fontFamily: 'Merriweather', lineHeight: 1.4 }}>
                  Encounter Difficulty
                </Typography>
                <EncounterDifficultyBar
                  adjustedXP={liveEncounter.adjustedXP}
                  levels={liveEncounter.levels}
                  partyFallbackSummary={
                    liveEncounter.usingPartyFallback
                      ? { partySize: dmParty.partySize, partyLevel: dmParty.partyLevel }
                      : null
                  }
                  onPartySettingsClick={() => setPartySettingsOpen(true)}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Friendly Section */}
        <StyledAccordion 
          expanded={friendlyExpanded} 
          onChange={() => setFriendlyExpanded(!friendlyExpanded)}
          disableGutters
        >
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
            tokentype="friendly"
          >
            <AccordionTitle>Friendly</AccordionTitle>
            <TokenCount>({friendlyTokens.length})</TokenCount>
          </StyledAccordionSummary>
          <AccordionDetails sx={{ padding: '8px 0' }}>
            {friendlyTokens.length > 0 ? (
              friendlyTokens.map(renderToken)
            ) : (
              <EmptyState>
                <EmptyText>No friendly tokens</EmptyText>
              </EmptyState>
            )}
          </AccordionDetails>
        </StyledAccordion>

        {/* Enemy Section - Only visible to creator/those with enemy permissions */}
        {canSeeEnemies && (
          <StyledAccordion 
            expanded={enemyExpanded} 
            onChange={() => setEnemyExpanded(!enemyExpanded)}
            disableGutters
          >
            <StyledAccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
              tokentype="enemy"
            >
              <AccordionTitle>Enemy</AccordionTitle>
              <TokenCount>({enemyTokens.length})</TokenCount>
            </StyledAccordionSummary>
            <AccordionDetails sx={{ padding: '8px 0' }}>
              {enemyTokens.length > 0 ? (
                enemyTokens.map(renderToken)
              ) : (
                <EmptyState>
                  <EmptyText>No enemy tokens</EmptyText>
                </EmptyState>
              )}
            </AccordionDetails>
          </StyledAccordion>
        )}
      </TokenList>

      <EncounterGeneratorModal
        open={encounterModalOpen}
        onClose={() => setEncounterModalOpen(false)}
        partyDefaults={dmParty}
        onPartyDefaultsChange={setDmParty}
        onSelectEncounter={(encounter) => {
          onAddEncounterMonsters?.(encounter);
          setEncounterModalOpen(false);
        }}
      />

      <DmPartySettingsModal
        open={partySettingsOpen}
        onClose={() => setPartySettingsOpen(false)}
        partySize={dmParty.partySize}
        partyLevel={dmParty.partyLevel}
        onSave={setDmParty}
      />

      <Menu
        anchorEl={enemyMenuAnchor}
        open={Boolean(enemyMenuAnchor)}
        onClose={closeEnemyMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            if (enemyMenuCoin) {
              setMonsterInfoCoin(enemyMenuCoin);
              setMonsterInfoOpen(true);
            }
            closeEnemyMenu();
          }}
        >
          <ListItemIcon>
            <MenuBookIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'secondary.main' }}>Stat block</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (enemyMenuCoin) {
              onDeleteToken?.(enemyMenuCoin);
            }
            closeEnemyMenu();
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <MonsterInfoModal
        open={monsterInfoOpen}
        onClose={() => {
          setMonsterInfoOpen(false);
          setMonsterInfoCoin(null);
        }}
        rawMonster={
          monsterInfoCoin?.monsterData?.index
            ? getMonsterRawByIndex(monsterInfoCoin.monsterData.index)
            : null
        }
        coin={monsterInfoCoin}
      />
    </PanelContainer>
  );
};

export default TokenPanel;
